import { useState } from 'react';
import { api } from '../../lib/api';
import { SearchSelect } from '../../components/SearchSelect';
import type { Bom, BomLine, Item } from '../../lib/types';

export function BomInputsEditor({ bom, onSaved }: { bom: Bom; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<BomLine[]>([]);
  const [ops, setOps] = useState(bom.operations ?? []);
  const [laborOnly, setLaborOnly] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function copyVersion() {
    if (!window.confirm('已批准 BOM 保留不变。复制为新版草稿后修改用料和工时，并重新审核批准？')) return;
    setBusy(true); setError('');
    try {
      const result = await api.post<{ data: Bom }>('/api/boms', {
        item_id: bom.item_id, quantity: bom.quantity, version: Number(bom.version) + 1,
        notes: `由 ${bom.number} 复制的新版本`,
        lines: bom.lines ?? [],
        operations: (bom.operations ?? []).map(o => ({ operation_id: o.operation_id, workstation: o.workstation, time_minutes: o.time_minutes, hourly_cost: o.hourly_cost })),
        no_material_required: !(bom.lines?.length), no_material_reason: '沿用原版无计划材料声明，需重新核对',
      });
      window.location.assign(`/boms?open=${encodeURIComponent(result.data.id)}`);
    } catch (e) { setError(e instanceof Error ? e.message : '复制失败'); setBusy(false); }
  }
  if (bom.status !== 'draft') return <div><button className="btn" disabled={busy} onClick={copyVersion}>{busy ? '复制中…' : '复制为新版，修改用料和工时'}</button>{error ? <p role="alert">{error}</p> : null}<p className="muted">已批准版本不直接修改，避免影响历史工单。新版需要重新审核和批准。</p></div>;
  function edit() { setLines((bom.lines ?? []).map(l => ({ ...l }))); setOps((bom.operations ?? []).map(o => ({ ...o }))); setLaborOnly(!(bom.lines?.length)); setReason(''); setError(''); setOpen(true); }
  async function save() {
    setBusy(true); setError('');
    try {
      await api.put(`/api/boms/${bom.id}/production-inputs`, {
        expected_inputs: JSON.stringify({ lines: bom.lines, operations: bom.operations }),
        lines: laborOnly ? [] : lines,
        operations: ops.map(o => ({ id: o.id, time_minutes: Number(o.time_minutes), hourly_cost: Number(o.hourly_cost) })),
        no_material_required: laborOnly, no_material_reason: reason,
      });
      setOpen(false); onSaved();
    } catch (e) { setError(e instanceof Error ? e.message : '保存失败'); }
    finally { setBusy(false); }
  }
  if (!open) return <button className="btn" onClick={edit}>编辑粉末 / 用料 / 工时</button>;
  return <section className="panel-body">
    <p>粉末列在用料中，只计入熔覆。机加工仅记录工时（分钟），不另加粉末或耗材。含熔覆和机加工的 BOM 请保留熔覆粉末。费用由后续财务模块核算。</p>
    <label><input type="checkbox" checked={laborOnly} onChange={e => setLaborOnly(e.target.checked)} disabled={busy} /> 本 BOM 为纯机加工，无计划材料，仅计工时</label>
    {laborOnly ? <input aria-label="无材料原因" placeholder="说明无材料原因（必填）" value={reason} onChange={e => setReason(e.target.value)} /> : <>
      {lines.map((line, index) => <div key={index} className="form-row">
        <SearchSelect<Item> endpoint="/api/items" value={line.item_id} label={`${line.item_code ?? ''} ${line.item_name_zh ?? ''}`.trim()} placeholder="选择粉末或材料" optionTitle={i => `${i.code} · ${i.name_zh}`} onChange={item => setLines(ls => ls.map((l, j) => j === index ? { ...l, item_id: item?.id ?? '', item_code: item?.code, item_name_zh: item?.name_zh } : l))} />
        <label>用量<input aria-label={`用量 ${index + 1}`} type="number" min="0" step="any" value={line.quantity} onChange={e => setLines(ls => ls.map((l, j) => j === index ? { ...l, quantity: Number(e.target.value) } : l))} /></label>
        <label>单位<input value={line.uom} onChange={e => setLines(ls => ls.map((l, j) => j === index ? { ...l, uom: e.target.value } : l))} /></label>
        <label>用于工序<input placeholder="例如：激光熔覆" value={line.operation_name ?? ''} onChange={e => setLines(ls => ls.map((l, j) => j === index ? { ...l, operation_name: e.target.value } : l))} /></label>
        <button className="btn" onClick={() => setLines(ls => ls.filter((_, j) => j !== index))}>删除用料</button>
      </div>)}
      <button className="btn" onClick={() => setLines(ls => [...ls, { id: '', bom_id: bom.id, item_id: '', quantity: 100, uom: 'g' }])}>添加粉末 / 材料</button>
    </>}
    {ops.map((op, index) => <div key={op.id} className="form-row">
      <strong>{op.operation_name}</strong>
      <label>工时（分钟）<input type="number" min="0" step="any" value={op.time_minutes} onChange={e => setOps(os => os.map((o, j) => j === index ? { ...o, time_minutes: Number(e.target.value) } : o))} /></label>
    </div>)}
    {error ? <p role="alert" className="notice bad">{error}</p> : null}
    <button className="btn primary" disabled={busy || (laborOnly && !reason.trim())} onClick={save}>{busy ? '保存中…' : '保存用料和工时'}</button>
    <button className="btn" disabled={busy} onClick={() => setOpen(false)}>取消</button>
  </section>;
}
